import * as React from 'react';
import {useEffect, useState} from 'react';
import {IntlProvider} from 'react-intl';
import {RouteProps, useLocation} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {Helmet, HelmetProvider} from 'react-helmet-async';

import {IStore} from 'redux/interface';
import {getAuthorizationAction} from 'redux/auth/authAsyncActions';
import {changeLocale} from 'redux/translation/translationAsyncActions';
import {
  firstAuthorizedRoute,
  Pages,
  routeTranslationKeys,
  TranslationKeys
} from 'utils/constants/routeConstants';
import {useGetTranslations} from 'utils/hooks/useGetTranslations';
import {SystemLanguage} from 'utils/constants/constants';
import {Bounce, ToastContainer} from 'react-toastify';
import {ISiteSettings} from '../../redux/auth/interfaces';
import {RubyChatLauncher} from 'components/ruby-chat-launcher/RubyChatLauncher';
import AppRoutes from './AppRoutes';

type PageMeta = {
  visible: boolean
  role?: string
}

const App: React.FunctionComponent<RouteProps> = () => {
  const {
    locale,
    translations
  } = useSelector((state: IStore) => state.translation);
  const {
    userProfile, settings
  } = useSelector((state: IStore) => state.auth);

  const dispatch = useDispatch();
  const {pathname} = useLocation();
  const [pages, setPages] = useState<Record<string, PageMeta>>();

  useGetTranslations({
    translationKey: TranslationKeys.Common
  });
  useGetTranslations({
    translationKey: TranslationKeys.Toasts
  });
  useGetTranslations({
    translationKey: routeTranslationKeys[firstAuthorizedRoute]
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
  }, [pathname]);

  useEffect(() => {
    if (userProfile && userProfile.language !== SystemLanguage.English) {
      dispatch(changeLocale(userProfile.language));
    }
  }, [userProfile, userProfile?.language]);

  useEffect(() => {
    dispatch(getAuthorizationAction());
  }, []);

  useEffect(() => {
    if (!settings) return;

    const normalize = (s: string) => s.trim().toLowerCase();

    const byName = settings.pages.reduce<Record<string, ISiteSettings['pages'][number]>>(
      (acc, p) => ({...acc, [normalize(p.name)]: p}),
      {}
    );

    const basePages = (Object.values(Pages) as Pages[]).reduce<Record<string, PageMeta>>((acc, page) => {
      const cfg = byName[normalize(String(page))];

      acc[String(page)] = {
        visible: Boolean(cfg),
        ...(cfg?.role ? {role: cfg.role} : {})
      };

      return acc;
    }, {});

    const contentPages = (settings.contentPages ?? []).reduce<Record<string, PageMeta>>((acc, cp) => {
      const key = `${Pages.Content}#${cp.contentId}`;

      acc[key] = {
        visible: true,
        ...(cp.role ? {role: cp.role} : {})
      };

      return acc;
    }, {});

    setPages({...basePages, ...contentPages});
  }, [settings]);

  return (
    <IntlProvider
      locale={locale}
      messages={translations[locale]}
      onError={(e) => {
        // Missing-translation warnings are expected noise here, not bugs: several locales have
        // intentionally incomplete translations (falling back to defaultMessage by design), and
        // there's also a brief unavoidable gap on first load where a translation file is still in
        // flight. Anything else (format errors, bad ICU syntax, etc.) still gets logged.
        if (e.code === 'MISSING_TRANSLATION') {
          return;
        }
        console.log('IntlError', e);
      }}
    >
      <ToastContainer
        role="alert"
        position="bottom-left"
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        hideProgressBar
        limit={5}
        autoClose={30000}
        transition={Bounce}
      />
      <HelmetProvider>
        <Helmet>
          <html lang={locale}/>
        </Helmet>

        <AppRoutes pages={pages} />
      </HelmetProvider>
      <RubyChatLauncher/>
    </IntlProvider>
  );
};

export default App;
